/**
 * Indicator computations for the replay view.
 *
 * These mirror the price-derived branches of `build_intraday_features` in
 * decision_docker/decision.py so the chart context approximates what the model
 * would have seen. They are NOT the values fed to the PPO — those come from
 * the persisted `obs_vector`. This module exists so the indicator trajectory
 * chart can be drawn from bars alone, without re-running the Python feature
 * builder server-side.
 */

import type { Bar } from "../api/client";

export interface IndicatorSeries {
  ts: string[];
  close: number[];
  ret_1?: (number | null)[];
  ret_12?: (number | null)[];
  ret_48?: (number | null)[];
  rv_12?: (number | null)[];
  rv_48?: (number | null)[];
  atr_norm?: (number | null)[];
  dist_vwap?: (number | null)[];
  tod_sin?: (number | null)[];
  tod_cos?: (number | null)[];
}

export function pctChange(values: number[], window: number): (number | null)[] {
  return values.map((v, i) =>
    i < window || values[i - window] === 0 ? null : (v - values[i - window]) / values[i - window],
  );
}

function nonNullSlice(arr: (number | null)[], from: number, to: number): number[] {
  const out: number[] = [];
  for (let i = from; i < to; i++) {
    const v = arr[i];
    if (v !== null && !Number.isNaN(v)) out.push(v);
  }
  return out;
}

export function rollingStd(values: (number | null)[], window: number): (number | null)[] {
  return values.map((_, i) => {
    if (i < window - 1) return null;
    const slice = nonNullSlice(values, i - window + 1, i + 1);
    if (slice.length < 2) return null;
    const mean = slice.reduce((a, b) => a + b, 0) / slice.length;
    const variance = slice.reduce((sum, v) => sum + (v - mean) ** 2, 0) / (slice.length - 1);
    return Math.sqrt(variance);
  });
}

export function atrNormalized(bars: Bar[], window: number): (number | null)[] {
  const tr: (number | null)[] = bars.map((b, i) => {
    if (i === 0) return null;
    const prevC = bars[i - 1].c;
    return Math.max(b.h - b.l, Math.abs(b.h - prevC), Math.abs(b.l - prevC));
  });
  return bars.map((b, i) => {
    if (i < window) return null;
    const slice = nonNullSlice(tr, i - window + 1, i + 1);
    if (slice.length < window) return null;
    const mean = slice.reduce((a, b) => a + b, 0) / slice.length;
    return b.c === 0 ? null : mean / b.c;
  });
}

export function distVwap(bars: Bar[], window: number): (number | null)[] {
  return bars.map((b, i) => {
    if (i < window - 1) return null;
    let tpv = 0;
    let vol = 0;
    for (let j = i - window + 1; j <= i; j++) {
      const t = (bars[j].h + bars[j].l + bars[j].c) / 3;
      tpv += t * bars[j].v;
      vol += bars[j].v;
    }
    if (vol === 0) return null;
    const vwap = tpv / vol;
    return vwap === 0 ? null : (b.c - vwap) / vwap;
  });
}

export function timeOfDay(bars: Bar[]): { sin: number[]; cos: number[] } {
  const RTH_MINUTES = 390;
  const sin: number[] = [];
  const cos: number[] = [];
  for (const b of bars) {
    const d = new Date(b.t);
    const mins = d.getUTCHours() * 60 + d.getUTCMinutes();
    sin.push(Math.sin((2 * Math.PI * mins) / RTH_MINUTES));
    cos.push(Math.cos((2 * Math.PI * mins) / RTH_MINUTES));
  }
  return { sin, cos };
}

/** Compute the full price-derived indicator series for the chart. */
export function computeIndicators(bars: Bar[]): IndicatorSeries {
  if (bars.length === 0) return { ts: [], close: [] };
  const close = bars.map((b) => b.c);
  const ret_1 = pctChange(close, 1);
  const series: IndicatorSeries = {
    ts: bars.map((b) => b.t),
    close,
    ret_1,
    ret_12: pctChange(close, 12),
    ret_48: pctChange(close, 48),
    rv_12: rollingStd(ret_1, 12),
    rv_48: rollingStd(ret_1, 48),
    atr_norm: atrNormalized(bars, 14),
    dist_vwap: distVwap(bars, 78),
  };
  const tod = timeOfDay(bars);
  series.tod_sin = tod.sin;
  series.tod_cos = tod.cos;
  return series;
}
